import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { type Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import type { Bundle } from '@adornis/buchungsheld-db/entities/Bundle.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { filter, tap } from 'rxjs';
import '../../../../components/bh-product-picker.js';
import { BHFormController } from '../../../../controller/BHFormController.js';

@customElement('bh-page-admin-einrichtung-bundle-modify')
export class BHPageAdminEinrichtungBundleModify extends BuchungsheldLitElement {
  @property({ attribute: false }) bundle = new RXController<Maybe<Bundle>>(this, undefined);
  @property({ attribute: false }) timeframes = new RXController<Maybe<Timeframe[]>>(this, undefined);
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @property({ attribute: 'mode', type: String }) mode!: 'add' | 'edit';

  @state() private readonly _bundleController = new BHFormController(
    this,
    this.bundle.observable.pipe(
      filter(Boolean),
      tap(bundle => {
        if (!bundle.publishDate) bundle.publishDate = DateTime.local(); // set default publishDate to today
      }),
    ),
    {
      success: async instance => {
        if (this.mode === 'add') await instance.create();
        if (this.mode === 'edit') await instance.save();

        return goTo(`/admin/institution-details/${this.institutionID.value}/bundles`);
      },
      error: err => {
        void XSnackbar.show(err.message);
        console.log('error', err);
      },
    },
  );

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    return this.bundle.value?.revert();
  }

  override render() {
    this.exposeAction('save', {
      icon: 'save',
      text: 'Speichern',
      callback: () => this._bundleController.validate(),
    });

    return html`
      <tr-flex space="md">
        <tr-input placeholder="Name" ${this._bundleController.field('name')}></tr-input>

        <x-input
          type="date"
          ${this._bundleController.field('publishDate')}
          placeholder="${this._translation.translate('field', 'publishDate')}*"
        ></x-input>

        <tr-dropdown-selection
          clearable
          ${this._bundleController.field('timeframeID')}
          placeholder=${this._translation.translate('timeframe', 'singular')}
          .selectables=${this.timeframes.value?.map(timeframe => timeframe._id) ?? []}
          .renderString=${(instanceID: string) =>
            this.timeframes.value?.find(timeframe => timeframe._id === instanceID)?.name ?? ''}
          @value-picked=${e => {
            (this._bundleController.document as any).productIDs = [];
            this.requestUpdate();
          }}
        >
        </tr-dropdown-selection>
        <bh-product-picker
          only-workshops
          no-overlapping
          placeholder="Termine"
          placeholder-mode="static-floating"
          .institutionID=${this.institutionID.value}
          .timeframeID=${this._bundleController.document?.timeframeID}
          ${this._bundleController.field('productIDs')}
        ></bh-product-picker>

        <x-flex horizontal crossaxis-center space="md">
          <x-checkbox
            label=${this._translation.translate('field', 'hasPrice')}
            ${this._bundleController.field('hasPrice')}
            @value-picked=${(e: ValueEvent<boolean>) => {
              if (e.detail.value && this._bundleController.document?.getUnitAmount) {
                this._bundleController.document.price.unitAmount = 0;
              }
              this.requestUpdate();
            }}
          ></x-checkbox>

          ${this._bundleController.document?.hasPrice
            ? html`
                <tr-formfield-wrapper placeholder=${this._translation.translate('field', 'unitAmount')}>
                  <tr-currency-input ${this._bundleController.field('price')} default=${0}></tr-currency-input>
                </tr-formfield-wrapper>
              `
            : nothing}
        </x-flex>

        <tr-checkbox .label=${'Ist Aktiv'} ${this._bundleController.field('isActive')}></tr-checkbox>
      </tr-flex>
    `;
  }
}
